import { SUMMARY_TYPE } from "../global/constants";

/**
 * Bryntum dependency records derived from schedule-activity rows. Links live in
 * `predecessorsTab` only — the successors column is never read back as a link.
 *
 * Lives outside the Gantt folder so redux actions can build links without importing
 * `components/Bryntum/Gantt/utils.js`, which imports the actions back.
 */
/**
 * A row the Gantt must not draw a link on. `summary` is the backend's own flag;
 * `lineType` is checked too because the rest of the app keys summary-ness off it
 * (taskRecordIsSummary) and a row can reach here from either shape.
 */
const rowIsSummary = (task) => Boolean(task.summary) || task.lineType === SUMMARY_TYPE;

export const getGanttDependencies = (items = []) => {
  // Summaries carry no dependencies. Filtering the SUCCESSOR side only still drew
  // every link whose PREDECESSOR was a récap, which is what put arrows on summary
  // bars. Only rows present in `items` can be judged: callers legitimately pass a
  // subset (Task Focus shows one resource's tasks), and a predecessor outside it
  // must keep its link rather than vanish.
  const summaryIds = new Set(
    items.filter(rowIsSummary).map((task) => `${task.id}`)
  );

  return items
    .filter((task) => !rowIsSummary(task))
    .reduce((dependencies, item) => {
      const predecessorsTab = Object.entries(item.predecessorsTab || {});
      predecessorsTab.forEach(([predecessorId, linkInfo]) => {
        if (summaryIds.has(`${predecessorId}`)) return;
        const from = parseInt(predecessorId);
        dependencies.push({
          id: `dep_${from}_${item.id}`,
          to: item.id,
          from,
          lag: linkInfo.lag,
          type: linkInfo.linkType,
          // Carried so a generated link can be told apart from one the user drew, and named in
          // its tooltip. The backend has always serialised these; the Gantt just dropped them.
          reason: linkInfo.reason ?? null,
          reasonEntityId: linkInfo.reasonEntityId ?? null,
        });
      });

      return dependencies;
    }, []);
};

/**
 * Draws the links carried by the given rows on a live Gantt, skipping the ones the
 * store already holds. Used right after a row is created so its links to the project
 * milestones show up without a refresh.
 */
export const addMissingDependenciesToGantt = (gantt, rows = []) => {
  const dependencyStore = gantt?.dependencyStore;
  if (!dependencyStore) return [];

  const existingIds = new Set(dependencyStore.getRange().map((dep) => dep.id));
  const missing = getGanttDependencies(rows).filter(
    (dep) => dep.from && dep.to && !existingIds.has(dep.id)
  );
  if (missing.length) dependencyStore.add(missing);
  return missing;
};

/**
 * Makes the Gantt's dependency store match `rows`: draws the links they carry that the store is
 * missing, and removes the ones they no longer carry.
 *
 * Only ever removes a link whose BOTH endpoints are among `rows`. Callers legitimately pass a
 * filtered subset (Task Focus shows one resource's tasks), and a link to a row outside that subset
 * is absent from the payload without being deleted.
 *
 * Adding without removing is what left cadencing arrows on the canvas after a re-simulation: the
 * backend had deleted the links, but the store only ever grew.
 */
export const reconcileGanttDependencies = (
  gantt,
  rows = [],
  { isEndpointBusy } = {}
) => {
  const dependencyStore = gantt?.dependencyStore;
  if (!dependencyStore) return { added: [], removed: [] };

  const next = getGanttDependencies(rows);
  const nextIds = new Set(next.map((dep) => dep.id));
  const current = dependencyStore.getRange();
  const currentIds = new Set(current.map((dep) => dep.id));
  const rowIds = new Set(rows.map((row) => `${row.id}`));

  // A link the user just drew is absent from `rows` until its own write and the
  // by-ids refresh of its two endpoints have landed, so reconciling against a
  // payload fetched in between took the arrow straight back off the canvas —
  // the server had stored it, and it reappeared only on reload. Skip any link
  // whose endpoints still have a write open.
  const endpointBusy = (dep) =>
    typeof isEndpointBusy === "function" &&
    (isEndpointBusy(dep.from) || isEndpointBusy(dep.to));

  const added = next.filter((dep) => !currentIds.has(dep.id));
  const removed = current.filter(
    (dep) =>
      !nextIds.has(dep.id) &&
      rowIds.has(`${dep.from}`) &&
      rowIds.has(`${dep.to}`) &&
      !endpointBusy(dep)
  );

  if (added.length) dependencyStore.add(added);
  if (removed.length) dependencyStore.remove(removed);
  return { added, removed };
};

/**
 * Puts a row's INCOMING links back to what the server holds.
 *
 * `removeDrawnDependency` below drops the one arrow a refused drop had drawn. The
 * task-edit dialog instead rewrites a row's WHOLE predecessor set, so there is no
 * single arrow to drop: a refused write left every link the dialog had added on the
 * canvas, claiming a dependency stored nowhere, until a reload.
 *
 * `rows` is the redux payload. The dialog never touches it — it edits the Bryntum
 * dependency store — so its `predecessorsTab` is still the server's answer, available
 * without another round-trip. And `predecessorsTab` lives on the SUCCESSOR, so ONE row
 * is authoritative for every link pointing at it. That is what makes this possible
 * where `reconcileGanttDependencies` is not: that one reconciles an arbitrary set of
 * rows, so it can only remove a link whose BOTH endpoints are in its payload, and the
 * dialog's payload names the successor alone.
 */
export const restoreIncomingDependencies = (gantt, rows = [], toId) => {
  const dependencyStore = gantt?.dependencyStore;
  if (!dependencyStore || toId == null) return { added: [], removed: [] };

  const row = (rows || []).find((item) => `${item.id}` === `${toId}`);
  if (!row) return { added: [], removed: [] };

  const stored = getGanttDependencies([row]);
  const storedById = new Map(stored.map((dep) => [`${dep.id}`, dep]));
  const drawn = dependencyStore
    .getRange()
    .filter((dep) => `${dep.to}` === `${toId}`);
  const drawnIds = new Set(drawn.map((dep) => `${dep.id}`));

  const removed = drawn.filter((dep) => !storedById.has(`${dep.id}`));
  const added = stored.filter((dep) => !drawnIds.has(`${dep.id}`));

  // A link the dialog only re-typed or re-lagged keeps its record rather than
  // being dropped and redrawn, so the engine reschedules once and the arrow
  // never blinks. Days, because that is what the payload and the backend speak.
  drawn.forEach((dep) => {
    const asStored = storedById.get(`${dep.id}`);
    if (!asStored) return;
    if (dep.type !== asStored.type) dep.type = asStored.type;
    if (dep.lag !== asStored.lag && typeof dep.setLag === "function") {
      dep.setLag(asStored.lag, "day");
    }
  });

  if (removed.length) dependencyStore.remove(removed);
  if (added.length) dependencyStore.add(added);
  return { added, removed };
};

/**
 * Removes a link the user drew but the server refused, so the arrow doesn't stay on
 * screen claiming a dependency that exists nowhere. Matches on the endpoints rather
 * than on a record reference: the drop handler runs before the store settles on an id.
 */
export const removeDrawnDependency = (gantt, fromId, toId) => {
  const dependencyStore = gantt?.dependencyStore;
  if (!dependencyStore) return false;

  const drawn = dependencyStore
    .getRange()
    .filter((dep) => `${dep.from}` === `${fromId}` && `${dep.to}` === `${toId}`);
  if (!drawn.length) return false;

  dependencyStore.remove(drawn);
  return true;
};
